import type { OrderLifecycleStage } from '@erp/shared-types';
import { T, useGT } from 'gt-react';
import { Badge } from 'src/components/ui/badge';
import { cn } from '@/lib/utils';
import { orderLifecycleStageLabels } from './orderLifecycleStageLabels';

/**
 * Rolled-up lifecycle stage of a draft sales order (spec §3 funnel), shared by
 * the v3 list table and the review-page stepper so the vocabulary never drifts.
 * `ready` uses a blue signal colour (not a neutral grey) to read as "act now".
 */
const BLUE = 'border-transparent bg-blue-50 text-blue-700';
const AMBER = 'border-transparent bg-amber-50 text-amber-700';

const STAGE: Record<
  OrderLifecycleStage,
  {
    variant: 'secondary' | 'warning' | 'success' | 'destructive' | 'outline';
    className?: string;
  }
> = {
  draft: { variant: 'secondary' },
  creating_discounts: { variant: 'outline', className: AMBER },
  approving: { variant: 'warning' },
  ready: { variant: 'outline', className: BLUE },
  created: { variant: 'success' },
  // Every line is an exception item: Customer Service owns it, so nothing for
  // the Distributor Manager to do — amber "exception", not destructive.
  under_cs_review: { variant: 'outline', className: AMBER },
  rejected: { variant: 'destructive' },
  // Replaced by a revised PO — muted, not destructive: nothing went wrong,
  // work simply continues on the newer revision.
  superseded: { variant: 'outline', className: 'text-muted-foreground' },
};

export function OrderLifecycleStageBadge({
  stage,
  awaitingRevision = false,
  className,
}: {
  stage: OrderLifecycleStage;
  /** Rejected while waiting on a replacement PO — labelled as such. */
  awaitingRevision?: boolean;
  className?: string;
}) {
  const cfg = STAGE[stage];
  const labels = orderLifecycleStageLabels(useGT());
  return (
    <Badge variant={cfg.variant} className={cn(cfg.className, className)}>
      {stage === 'rejected' && awaitingRevision ? (
        <T context="order status badge: rejected, a new purchase order is expected">
          Rejected · awaiting new PO
        </T>
      ) : (
        labels[stage]
      )}
    </Badge>
  );
}
